import React, { useCallback, useEffect, useRef, useState } from "react";
import { X } from "@phosphor-icons/react";
import { MONEY_TIPS, nextTipIndex } from "../../lib/moneyTips";

/**
 * MoneyTips — Currency-page tip surfacing (Build 85, D-026)
 * =========================================================
 * Split into two exports:
 *
 *   • <MoneyTipButton onClick /> — the 💡 icon button.
 *     Style matches the Currency header's circular white buttons.
 *   • <MoneyTipSheet open onOpenChange /> — the "Good to know"
 *     popover panel. Controlled component. Renders inline near the
 *     caller (no portal) so the surrounding scroll region absorbs it.
 *
 * A compound convenience wrapper is still exported for callers that
 * want the button+panel bundle:
 *   • <MoneyTipPopover /> — internal state, renders both stacked.
 *
 * Contract:
 *   - Each `open` transition (closed → open) advances to a new tip
 *     (never consecutively repeats).
 *   - ≥ 44×44pt tap target on the button (Apple HIG).
 *   - aria-label / aria-expanded / aria-controls all wired.
 *   - Zero forbidden Apple copy in the tip list.
 *   - Closes on: X, outside tap, Escape, swipe-down where supported.
 */

export function MoneyTipButton({ open = false, onClick, className = "" }) {
  // Use the actual 💡 emoji glyph so it renders as the platform-native
  // yellow lightbulb (matches iOS system emoji rendering). When the
  // panel is open we bump it slightly and add a soft yellow ring so
  // the trigger's "on" state is unambiguous.
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Money tips"
      aria-expanded={open}
      aria-controls="money-tip-panel"
      data-testid="currency-money-tips-button"
      className={
        "w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95 " +
        (open
          ? "bg-yellow-100 ring-2 ring-yellow-400 shadow-soft "
          : "bg-white shadow-soft hover:bg-navy/[0.02] ") +
        className
      }
    >
      <span
        aria-hidden="true"
        className="text-[20px] leading-none select-none"
        style={{ filter: open ? "none" : "grayscale(0)" }}
      >
        💡
      </span>
    </button>
  );
}

export function MoneyTipSheet({ open, onOpenChange }) {
  const [tipIndex, setTipIndex] = useState(() =>
    Math.floor(Math.random() * MONEY_TIPS.length),
  );
  const panelRef = useRef(null);
  const wasOpenRef = useRef(false);
  const touchStartYRef = useRef(null);

  // Advance the tip each time the sheet transitions closed → open.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setTipIndex((prev) => nextTipIndex(prev));
    }
    wasOpenRef.current = open;
  }, [open]);

  const close = useCallback(() => onOpenChange?.(false), [onOpenChange]);

  // Escape + outside click / tap
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") close();
    }
    function onOutside(e) {
      const p = panelRef.current;
      if (!p) return;
      if (p.contains(e.target)) return;
      // Let the button click cycle through its own onClick handler.
      const trigger = document.querySelector('[data-testid="currency-money-tips-button"]');
      if (trigger && trigger.contains(e.target)) return;
      close();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside, { passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [open, close]);

  // Swipe-down to close
  const onTouchStart = useCallback((e) => {
    touchStartYRef.current = e.touches[0]?.clientY ?? null;
  }, []);
  const onTouchEnd = useCallback(
    (e) => {
      const start = touchStartYRef.current;
      touchStartYRef.current = null;
      if (start == null) return;
      const end = e.changedTouches[0]?.clientY ?? start;
      if (end - start > 40) close();
    },
    [close],
  );

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      id="money-tip-panel"
      role="region"
      aria-label="Good to know"
      data-testid="money-tip-panel"
      data-tiplingo-safe="1"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="mt-3 rounded-2xl bg-orange/5 border border-orange/20 px-4 pt-6 pb-4 relative animate-in fade-in slide-in-from-top-1 duration-200"
    >
      {/* Drag affordance */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-orange/25" />
      <button
        type="button"
        onClick={close}
        aria-label="Close tips"
        data-testid="money-tip-close"
        className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-navy-soft hover:text-navy hover:bg-navy/5 transition"
      >
        <X size={16} />
      </button>
      <div className="font-jakarta font-bold text-[11px] uppercase tracking-[0.18em] text-orange mb-1">
        Good to know
      </div>
      <p
        className="font-jakarta text-sm text-navy leading-snug pr-6 max-h-56 overflow-y-auto"
        data-testid="money-tip-text"
      >
        {MONEY_TIPS[tipIndex]}
      </p>
    </div>
  );
}

/** Legacy compound — internal state, button+panel stacked. */
export default function MoneyTipPopover() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <MoneyTipButton open={open} onClick={() => setOpen((v) => !v)} />
      <MoneyTipSheet open={open} onOpenChange={setOpen} />
    </>
  );
}
